import useSWR from 'swr';

import myShopClient from '@services/nearlive';

/**
 * Inventory source list for the shop. Returns whatever the API resolves with
 * — no frontend fallback. The seeded shop should have an `RBA` source so
 * everything Inventory renders against. Pages render an empty / loading
 * state when the list is empty.
 */
export function useInventorySources(shopId) {
	const key = shopId ? ['unify:inventory-sources', shopId] : null;
	const { data, error, isLoading, mutate } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const { data } = await client.get(`/api/inventory-sources/${shopId}`);
			return data;
		},
		{ revalidateOnFocus: false }
	);
	return { sources: Array.isArray(data) ? data : [], error, isLoading, mutate };
}

export function useStockSummary(shopId) {
	const key = shopId ? ['unify:stock-summary', shopId] : null;
	const { data, error, isLoading } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const { data } = await client.get(`/api/stock-summary/${shopId}`);
			return data;
		},
		{ revalidateOnFocus: false }
	);
	return { summary: data, error, isLoading };
}

/**
 * Single endpoint backing the Inventory landing page: connection status,
 * total / in-stock counts, per-channel visibility breakdown, and
 * profile-completeness gaps by attribute. Backed by `inventory-page-summary`
 * (`/api/inventory/{shopId}/page-summary`) — the Typesense-backed counts
 * may render as zeros locally until stock items are synced into the dev
 * Typesense collection.
 *
 * Pass `channelId` to scope the summary to a single channel: counts are
 * filtered to items live on that channel, and profile-completeness only
 * lists the gaps that matter for it (per `@nearst/inventory-issues`).
 */
export function useInventoryPageSummary(shopId, channelId) {
	const key = shopId ? ['unify:inventory-page-summary', shopId, channelId ?? null] : null;
	const { data, error, isLoading, mutate } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const { data } = await client.get(
				`/api/inventory/${shopId}/page-summary`,
				channelId ? { params: { channel: channelId } } : undefined
			);
			return data;
		},
		{ revalidateOnFocus: false }
	);
	return { summary: data ?? null, error, isLoading, mutate };
}

/**
 * A single stock record for the Product detail page. Backed by
 * `get-stock-record` (`GET /api/inventory/{shopId}/{barcode}`). Resolves to
 * null when the barcode isn't in the shop's catalogue.
 */
export function useStockRecord(shopId, barcode) {
	const key = shopId && barcode ? ['unify:stock-record', shopId, barcode] : null;
	const { data, error, isLoading, mutate } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const { data } = await client.get(`/api/inventory/${shopId}/${encodeURIComponent(barcode)}`);
			return data;
		},
		{ revalidateOnFocus: false }
	);
	return { record: data ?? null, error, isLoading, mutate };
}

/**
 * Feed-quality counts for a shop's catalogue — `totalItems`, `activeItems`,
 * `erroneousItems`, `inactiveItems`. Backed by
 * `GET /api/inventory/{shopId}/feed-quality-stats`. Drives the
 * all-stock-rejected health banner (`erroneousItems === totalItems &&
 * totalItems > 0`).
 */
export function useFeedQuality(shopId) {
	const key = shopId ? ['unify:feed-quality', shopId] : null;
	const { data, error, isLoading } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const { data } = await client.get(`/api/inventory/${shopId}/feed-quality-stats`);
			return data;
		},
		{ revalidateOnFocus: false }
	);
	return { stats: data ?? null, error, isLoading };
}

/**
 * Per-issue counts + ingest error summary for the Inventory accuracy page.
 * Backed by `inventory-status` (`GET /api/inventory/{shopId}/inventory-status`).
 */
export function useInventoryStatus(shopId) {
	const key = shopId ? ['unify:inventory-status', shopId] : null;
	const { data, error, isLoading } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const { data } = await client.get(`/api/inventory/${shopId}/inventory-status`);
			return data;
		},
		{ revalidateOnFocus: false }
	);
	return { issues: Array.isArray(data) ? data : [], error, isLoading };
}

/**
 * 30-day stock upload history for the Inventory accuracy page's uptime strip
 * and chart. Queries the insights `stock_count` table via the generic
 * `POST /api/insights` endpoint — same path the retailers dashboard's
 * feed-health card uses.
 */
export function useUploadHistory(shopId) {
	const key = shopId ? ['unify:upload-history', shopId] : null;
	const { data, error, isLoading } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const to = new Date();
			const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
			const iso = (d) => d.toISOString().slice(0, 10);
			const { data } = await client.post('/api/insights', {
				id: [shopId],
				table: 'stock_count',
				columns: ['date', 'in_stock_barcodes', 'total_uploaded_barcodes'],
				from: iso(from),
				to: iso(to),
				sort: [{ column: 'date', order: 'asc' }]
			});
			return data;
		},
		{ revalidateOnFocus: false }
	);
	const rows = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];
	return { rows, error, isLoading };
}

/**
 * Aisle / shelf autocomplete options for a product's delivery menu category.
 * Backed by `get-menu-category-options` (`GET /api/{shopId}/menu-category-options`).
 * Shelf options are scoped to the currently-selected `aisle`.
 */
export function useMenuCategoryOptions(shopId, aisle) {
	const key = shopId ? ['unify:menu-category-options', shopId, aisle ?? ''] : null;
	const { data, isLoading } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			// Always send `aisle` (even empty) — the handler reads
			// `queryStringParameters.aisle` unconditionally and 500s otherwise.
			const { data } = await client.get(`/api/${shopId}/menu-category-options`, {
				params: { aisle: aisle ?? '' }
			});
			return data;
		},
		{ revalidateOnFocus: false }
	);
	return {
		aisleOptions: Array.isArray(data?.aisleOptions) ? data.aisleOptions : [],
		shelfOptions: Array.isArray(data?.shelfOptions) ? data.shelfOptions : [],
		isLoading
	};
}

/**
 * Mutations for the inventory sub-pages — thin wrappers over the shared
 * api-handlers. None are SWR-cached; callers revalidate the relevant query
 * via the `mutate` returned from the read hooks.
 */
export async function editProduct(shopId, barcode, patch) {
	const client = await myShopClient();
	const { data } = await client.put(`/api/inventory/${shopId}/${encodeURIComponent(barcode)}`, patch);
	return data;
}

export async function setProductChannels(shopId, barcodes, newChannels) {
	const client = await myShopClient();
	const { data } = await client.put(`/api/inventory/channels/${shopId}`, { barcodes, newChannels });
	return data;
}

export async function setQuantityBuffer(shopId, quantityBuffer) {
	const client = await myShopClient();
	const { data } = await client.put(`/api/inventory/${shopId}/quantity-buffer`, { quantityBuffer });
	return data;
}

export async function searchProductCategories(query) {
	const client = await myShopClient();
	const { data } = await client.get('/api/search-product-categories', { params: { query } });
	return Array.isArray(data) ? data : [];
}
